import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { P3 } from '@/components/ui/text';
import { useLocalizedDayjs } from '@/languages/dayjs';
import { toIsoDate } from '@/lib/datetime-helpers';
import { PopoverAnchor } from '@radix-ui/react-popover';
import * as React from 'react';
import { TableCell } from '../table-cell';

function formatDate(dayjs: ReturnType<typeof useLocalizedDayjs>, value: string) {
  return dayjs(value)
    .format('MMM Do YYYY, h:mma')
    .replace(/\s+(am|pm)/i, (_match, period) => period.toLowerCase());
}

interface DateCellProps {
  value?: string | null;
}

export function DateCell({ value }: DateCellProps) {
  const dayjs = useLocalizedDayjs();

  if (!value) {
    return null;
  }

  return <P3 className="truncate text-primary-60">{formatDate(dayjs, value)}</P3>;
}

export interface EditableDateCellHandle {
  open: () => void;
}

interface EditableDateCellProps {
  value?: string | null;
  onChange?: (value: string) => void;
}

export const EditableDateCell = React.memo(
  React.forwardRef<EditableDateCellHandle, EditableDateCellProps>(function EditableDateCell(
    { value, onChange },
    ref,
  ) {
    const dayjs = useLocalizedDayjs();
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);

    const formatted = value ? formatDate(dayjs, value) : null;
    const selectedDate = value ? new Date(value) : undefined;

    React.useImperativeHandle(
      ref,
      () => ({
        open: () => setOpen(true),
      }),
      [],
    );

    const handleSelect = (date: Date | undefined) => {
      if (date && onChange) {
        onChange(toIsoDate(date));
        setOpen(false);
      }
    };

    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        if (newOpen === open) {
          return;
        }
        setOpen(newOpen);
        if (!newOpen) {
          const cell = anchorRef.current?.closest('[tabindex]') as HTMLElement | null;
          if (cell) {
            requestAnimationFrame(() => cell.focus());
          }
        }
      },
      [open],
    );

    return (
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverAnchor ref={anchorRef} onDoubleClick={() => setOpen(true)}>
          <TableCell className="h-10">
            {formatted ? <P3 className="truncate text-primary-60">{formatted}</P3> : null}
          </TableCell>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={-41}
          className="w-auto rounded-2xl border border-primary-5 bg-white p-0 shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          <Calendar mode="single" selected={selectedDate} onSelect={handleSelect} />
        </PopoverContent>
      </Popover>
    );
  }),
);
